import React, { useEffect, useState, useCallback } from 'react';
import { Megaphone, X, ArrowRight, Bell } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

interface GlobalAnnouncementBannerProps {
    className?: string;
}

/**
 * Generates a simple hash validation for the announcement message.
 * Used to track unique dismissals. If the message changes, the hash changes,
 * and the banner will reappear.
 */
const generateMessageHash = (text: string): string => {
    let hash = 0;
    if (text.length === 0) return hash.toString();
    for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
};

const GlobalAnnouncementBanner: React.FC<GlobalAnnouncementBannerProps> = ({ className }) => {
    const [message, setMessage] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // Fetch message from API
    const fetchAnnouncement = useCallback(async () => {
        try {
            const response = await fetch('/api/system/announcement');
            if (response.ok) {
                const data = await response.json();

                if (data.message) {
                    const msgHash = generateMessageHash(data.message);
                    const dismissedHash = localStorage.getItem('announcement_dismissed_hash');

                    // Show if we have a message AND (it's new OR it hasn't been dismissed)
                    // Logic: If the current message hash matches the stored dismissed hash, don't show.
                    if (data.message !== message) {
                        setMessage(data.message);
                        if (dismissedHash !== msgHash) {
                            setIsVisible(true);
                        } else {
                            // Already dismissed this specific message
                            setIsVisible(false);
                        }
                    }
                } else {
                    setIsVisible(false);
                    setMessage(null);
                }
            }
        } catch (error) {
            console.error('Failed to fetch global announcement', error);
        }
    }, [message]);

    useEffect(() => {
        // Initial fetch
        fetchAnnouncement();

        // Polling interaction - check every 5 minutes
        const interval = setInterval(fetchAnnouncement, 300000);
        return () => clearInterval(interval);
    }, [fetchAnnouncement]);

    const handleDismiss = () => {
        if (message) {
            setIsVisible(false);
            const msgHash = generateMessageHash(message);
            localStorage.setItem('announcement_dismissed_hash', msgHash);
        }
    };

    if (!message) return null;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} // Apple-esque ease
                    className={`relative z-50 flex-shrink-0 ${className}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Modern Glass/Dark Background */}
                    <div className="relative w-full bg-[#09090b]/90 backdrop-blur-xl text-white border-b border-white/[0.08] overflow-hidden">

                        {/* 1. Subtle Aurora Gradient / Glow Effect */}
                        <div
                            className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-indigo-500/5 to-purple-500/5 opacity-50 pointer-events-none"
                            aria-hidden="true"
                        />

                        {/* 2. Top Highlight Line for Depth */}
                        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-60" />

                        {/* Content Container */}
                        <div className="max-w-[1400px] mx-auto px-4 py-3 sm:px-6 lg:px-8 relative">
                            <div className="flex items-center justify-between gap-4 sm:gap-6">

                                {/* Left Content: Icon + Text */}
                                <div className="flex-1 flex items-start sm:items-center min-w-0 gap-3 sm:gap-4">

                                    {/* Icon Badge */}
                                    <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-gray-300 border border-white/10 shadow-[0_0_15px_-3px_rgba(255,255,255,0.05)]">
                                            <Bell className="w-4 h-4" />
                                        </span>
                                    </div>

                                    {/* Text Content */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-[13px] sm:text-[14px] leading-tight sm:leading-normal">
                                        <div className="flex items-center gap-2">
                                            {/* Dynamic Badge - Only show if we detect specific keywords */}
                                            {/^(New|Feature|Update|Alert|Important)/i.test(message) && (
                                                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wider bg-white/10 text-gray-300 border border-white/5">
                                                    {message.split(' ')[0]}
                                                </span>
                                            )}
                                            {/^(New|Feature|Update|Alert|Important)/i.test(message) && (
                                                <span className="sm:hidden font-semibold text-teal-400 text-xs uppercase tracking-wide">
                                                    {message.split(' ')[0]}
                                                </span>
                                            )}
                                        </div>

                                        <p className="font-medium text-gray-200 tracking-wide">
                                            {/* Remove the keyword from the start of the message if we used it as a badge to avoid duplication, otherwise just show message */}
                                            {/^(New|Feature|Update|Alert|Important)/i.test(message) ? message.substring(message.indexOf(' ') + 1) : message}
                                        </p>

                                        {/* "Learn More" Link - Desktop */}
                                        <button className="hidden text-xs sm:inline-flex items-center gap-1 font-medium text-gray-400 hover:text-white transition-colors group cursor-pointer ml-1">
                                            <span className="underline decoration-gray-600 underline-offset-2 group-hover:decoration-white/50 transition-all">
                                                Learn more
                                            </span>
                                            <ArrowRight className="w-3 h-3 transition-transform duration-300 ease-out group-hover:translate-x-0.5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Right Actions: Mobile Link + Dismiss */}
                                <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">

                                    {/* "Learn More" Link - Mobile Only (Icon) */}
                                    <button className="sm:hidden p-1.5 -mr-2 text-gray-400 active:text-white active:bg-white/10 rounded-md transition-colors">
                                        <ArrowRight className="w-4 h-4" />
                                    </button>

                                    {/* Separator - Desktop */}
                                    <div className="hidden sm:block w-px h-4 bg-white/10" />

                                    {/* Dismiss Button */}
                                    <button
                                        onClick={handleDismiss}
                                        className="group relative p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                        aria-label="Dismiss announcement"
                                    >
                                        <X className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default GlobalAnnouncementBanner;
